import {
  Box,
  Pagination,
  PartnerOnboardingEntity,
  PartnerOnboardingEntityRequest,
  Skeleton,
  parseUTCDateTime,
  withStyles,
} from 'vopay';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { BodyText, Button, Table, TransactionStatus } from 'UI';
import usePartnerAccountStatusLabel from '@/hooks/usePartnerAccountStatusLabel';
import formatShortDate from '@/components/OnboardingTracker/formatShortDate';
import useEntityTypeLabels from '@/components/OnboardingTracker/useEntityTypeLabels';
import styles from './OnboardingTable.module.scss';

const cn = withStyles(styles);

export type OnboardingTableProps = {
  /**
   * The current page's rows, newest first, exactly as the table should render them. `DateAdded` and
   * `StatusDate` are UTC and nullable: no date (`DateAdded`) or no Since line (`StatusDate`) when `null`.
   */
  entities: PartnerOnboardingEntity[];
  /** The page is being (re)fetched: render a skeleton in place of the rows, never the blocking loader. */
  isLoading: boolean;
  /** Zero-based. */
  pageIndex: number;
  /** 0 or 1 means no pagination control. */
  pageCount: number;
  onPageChange: (pageIndex: number) => void;
  /**
   * The row's Account Name or its Parent Account (`Parent` carries the parent's `EntityType` +
   * `EntityID`) was clicked: the page opens that entity's Account or Partner Details drawer (ID-14136).
   */
  onOpenEntityDetails: (entity: PartnerOnboardingEntityRequest) => void;
  /** Off: partner names render as plain text, since Partner Details shows the partner's API credentials. */
  canOpenPartnerDetails: boolean;
  /** Track Progress was clicked: the page opens the entity's Application Details drawer. */
  onTrackProgress: (entity: PartnerOnboardingEntityRequest) => void;
};

export default function OnboardingTable({
  entities,
  isLoading,
  pageIndex,
  pageCount,
  onPageChange,
  onOpenEntityDetails,
  canOpenPartnerDetails,
  onTrackProgress,
}: OnboardingTableProps) {
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'OnboardingTracker.OnboardingTable' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();
  const { resolveEntityTypeLabel } = useEntityTypeLabels();

  // Figma shows Date Added as 2026-08-17: numeric, so the same in both locales.
  function formatDateAdded(dateAdded: string | null) {
    const parsed = dateAdded ? parseUTCDateTime(dateAdded) : null;

    return parsed ? format(parsed, 'yyyy-MM-dd') : '';
  }

  function renderStatus(entity: PartnerOnboardingEntity) {
    // Judgement call (spec §6.4 assumes a date): `StatusDate` is null when earthnode has no onboarding
    // history row for the current status, and a "Since" with no date says nothing, so the line is left out.
    const statusDate = entity.StatusDate ? parseUTCDateTime(entity.StatusDate) : null;

    return (
      <div className={cn('stackedCell')}>
        {/* Every row is an onboarding stage, so the chip is always the grey pending variant (Figma). */}
        <TransactionStatus status="pending" label={resolveStatusLabel(entity.Status)} />

        {/* No waiting-on indicator (Figma's "· With VoPay" is out of scope, spec §6.4). */}
        {statusDate && (
          <span className={cn('caption', 'tertiary')}>
            {t('since', { defaultValue: 'Since {{date}}', date: formatShortDate(statusDate, i18n.language) })}
          </span>
        )}
      </div>
    );
  }

  function renderParent({ Parent: parent }: PartnerOnboardingEntity) {
    if (!parent) return <BodyText>{t('direct', { defaultValue: 'Direct' })}</BodyText>;

    return renderEntityName(parent, parent.Name);
  }

  function renderEntityName({ EntityType, EntityID }: PartnerOnboardingEntityRequest, name: string) {
    // The backend sends '' for an entity without an APIAccountID: no details can be looked up for it.
    if (!EntityID || (EntityType === 'Partner' && !canOpenPartnerDetails)) return <BodyText>{name}</BodyText>;

    return <Table.LinkButton onClick={() => onOpenEntityDetails({ EntityType, EntityID })}>{name}</Table.LinkButton>;
  }

  return (
    <>
      {renderTable()}

      {pageCount > 1 && (
        <Box width="100%" jc="center" mt="lg" mb="sm">
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={({ selected }) => onPageChange(selected)}
          />
        </Box>
      )}
    </>
  );

  function renderTable() {
    // Page data, so a skeleton and never the blocking loader (loading-conventions).
    if (isLoading) return <Skeleton height="292px" />;

    return (
      <>
        <Table
          id="onboarding-tracker-table"
          zebra
          columns={[
            {
              id: 'dateAdded',
              header: t('dateAdded', { defaultValue: 'Date Added' }),
              align: 'left',
              template: ({ data }) => <BodyText>{formatDateAdded(data.DateAdded)}</BodyText>,
            },
            {
              id: 'accountName',
              header: t('accountName', { defaultValue: 'Account Name' }),
              align: 'left',
              template: ({ data }) => (
                <div className={cn('stackedCell')}>
                  {renderEntityName(data, data.Name)}
                  <span className={cn('caption', 'tertiary')}>{resolveEntityTypeLabel(data.EntityType)}</span>
                </div>
              ),
            },
            {
              id: 'parentAccount',
              header: t('parentAccount', { defaultValue: 'Parent Account' }),
              align: 'left',
              template: ({ data }) => renderParent(data),
            },
            {
              id: 'status',
              header: t('status', { defaultValue: 'Onboarding Status' }),
              align: 'left',
              template: ({ data }) => renderStatus(data),
            },
            {
              id: 'actions',
              header: tGlobal('actions'),
              align: 'left',
              template: ({ data }) =>
                data.Status === 'Application Pending' ? (
                  <Button
                    size="small"
                    onClick={() => onTrackProgress({ EntityType: data.EntityType, EntityID: data.EntityID })}
                  >
                    {t('trackProgress', { defaultValue: 'Track Progress' })}
                  </Button>
                ) : (
                  <BodyText>-</BodyText>
                ),
            },
          ]}
          rows={entities.map((entity) => ({ id: `${entity.EntityType}-${entity.EntityID}`, data: entity }))}
        />

        {/* ID-14141: the header stays when nothing matches. Judgement call (spec and Figma have no empty
            frame): the app's usual centred "no records" line, under the header. */}
        {entities.length === 0 && (
          <Box ai="center" jc="center" pt="md">
            <BodyText>{t('noRecords', { defaultValue: 'No entities found.' })}</BodyText>
          </Box>
        )}
      </>
    );
  }
}
